import { EntityTypes } from '@/constants/entityTypes'
import businessApi from '@/services/calls/business'
import { Business, BusinessesResponse, CreateUpdateBusinessPayload } from '@/types/business'
import { StorageSerializers, useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

interface States {
  businesses: Ref<BusinessesResponse | null>
  selectedBusiness: Ref<Business | null>
}

export const useBusinessStore = defineStore('business', () => {
  const states: States = {
    // Note: https://vueuse.org/core/useStorage/#custom-serialization
    // when you provide null as the default value, useStorage can't assume the data type from it.
    // In this case, you can provide a custom serializer or reuse the built-in ones explicitly.
    businesses: useStorage('businesses', null, undefined, {
      serializer: StorageSerializers.object,
    }),
    selectedBusiness: useStorage('selectedBusiness', null, undefined, {
      serializer: StorageSerializers.object,
    }),
  }

  const getSelectedBusinessAccountTypeId = computed(() => {
    // Guard the array access: a selected business can have an empty selected_account_types
    // (e.g. a business with no account type assigned yet — common in dev/staging seed data).
    // The old `selected_account_types[0].id` threw a TypeError in that case, and because this
    // getter runs inside the router beforeEach guard it aborted the navigation entirely —
    // e.g. a jump-to-business-admin would hang on the loading screen and never reach Admin.
    return states.selectedBusiness.value?.selected_account_types?.[0]?.id ?? EntityTypes.BRAND // Note: Fall back to the brand entity type when business/account-type data is absent
  })

  const createBusiness = async (payload: CreateUpdateBusinessPayload) => {
    const response = await businessApi.createBusiness(payload)
    states.businesses.value?.results.push(response)
  }

  const updateBusiness = async (businessId: number, payload: CreateUpdateBusinessPayload) => {
    const response = await businessApi.updateBusiness(businessId, payload)

    const businessToUpdate = states.businesses.value?.results.find((business) => business.id === response.id)
    if (businessToUpdate) {
      const {
        name,
        address,
        region,
        country,
        zip_code,
        phone,
        email,
        website,
        description,
        logo,
        selected_account_types,
      } = response

      businessToUpdate.name = name
      businessToUpdate.address = address
      businessToUpdate.region = region
      businessToUpdate.country = country
      businessToUpdate.zip_code = zip_code
      businessToUpdate.phone = phone
      businessToUpdate.email = email
      businessToUpdate.website = website
      businessToUpdate.description = description
      businessToUpdate.logo = logo
      businessToUpdate.selected_account_types = selected_account_types
    }
  }

  const resetStore = () => {
    for (const key in states) {
      states[key as keyof States].value = null
    }
  }

  return {
    ...states,
    getSelectedBusinessAccountTypeId,
    createBusiness,
    updateBusiness,
    resetStore,
  }
})
